<script setup lang="ts">
import { ref, computed } from 'vue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { sortBy } from 'lodash-es';

const props = defineProps<{
  facets: Array<{
    handle: string;
    name: string;
    options: Array<{ id: string; name: string; count: number }>
  }>,
  modelValue: Record<string, string[]>,
  loading: boolean
}>();

const emit = defineEmits(['update:modelValue']);

// Using a computed property to allow v-model two-way binding.
const selectedFilters = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue);
  }
});

const openFilters = ref<Record<string, boolean>>({});

function isSelected(filterHandle: string, optionValue: string) {
  return selectedFilters.value[filterHandle]?.includes(optionValue);
}

function toggleOption(filterHandle: string, optionValue: string) {
  // Clone the current filters so that we trigger a reactivity update.
  const filters = { ...selectedFilters.value };
  if (!filters[filterHandle]) {
    filters[filterHandle] = [];
  }
  const index = filters[filterHandle].indexOf(optionValue);
  if (index > -1) {
    filters[filterHandle] = filters[filterHandle].filter(val => val !== optionValue);
  } else {
    filters[filterHandle] = [...filters[filterHandle], optionValue];
  }
  selectedFilters.value = filters;
}
</script>

<template>
  <Accordion type="multiple" class="w-full" collapsible>
    <AccordionItem
      v-for="filter in sortBy(facets, 'name')"
      :key="`facet-${filter.handle}`"
      :value="filter.name"
      class="py-3 border-b"
    >
      <div class="flex flex-col gap-3 text-sm">
        <div class="flex items-center justify-between cursor-pointer">
          <span class="uppercase font-bold">{{ filter.name }}</span>
        </div>

        <!-- Render the first four options -->
        <div class="select-none flex flex-col gap-2 text-sm">
          <label
            v-for="(option, index) in filter.options?.slice(0, 4)"
            :key="`filter-${filter.handle}-${index}`"
            class="flex items-center gap-2"
          >
            <input
              class="w-4 h-4 rounded text-primary"
              type="checkbox"
              :checked="isSelected(filter.handle, option.id)"
              @change="toggleOption(filter.handle, option.id)"
              :disabled="loading"
            />
            <span>
              <span class="font-medium">{{ option.name }}</span>
              ({{ option.count }})
            </span>
          </label>
        </div>
      </div>

      <!-- Additional options in accordion content -->
      <AccordionContent class="text-sm">
        <div v-if="filter.options.length > 4" class="pt-2 flex flex-col gap-2">
          <label
            v-for="(option, index) in filter.options.slice(4)"
            :key="`filter-${filter.handle}-${index + 4}`"
            class="flex items-center gap-2"
          >
            <input
              class="w-4 h-4 rounded text-primary"
              type="checkbox"
              :checked="isSelected(filter.handle, option.id)"
              @change="toggleOption(filter.handle, option.id)"
            />
            <span>
              <span class="font-medium">{{ option.name }}</span>
              ({{ option.count }})
            </span>
          </label>
        </div>
      </AccordionContent>

      <!-- Accordion trigger for show more/less -->
      <AccordionTrigger
        v-if="filter.options.length > 4"
        class="!text-xs text-primary font-semibold pt-2 pb-0 cursor-pointer"
        @click="openFilters[filter.name] = !openFilters[filter.name]"
      >
        {{ openFilters[filter.name] ? $t('products.show_less') : $t('products.show_more') }}
      </AccordionTrigger>
    </AccordionItem>
  </Accordion>
</template>
